/**
 * Layout model for the audit trail Gantt (waterfall) view.
 *
 * Turns enriched audit entries into positioned bars on a shared time axis
 * (timelines.corp.amazon.com style): one row per task, bar spanning the
 * task's created_at -> completed_at, with the tail of the bar marking the
 * actual execution window when the backend stamped one. Pure functions so
 * the positioning math is unit-testable without rendering.
 */

import type { AuditEntry } from '@amzn/helianthus-arbiter-client';
import { parseEnrichedFields, type Stage } from './auditTrailStages';

export interface GanttTask {
  /** Human-readable label from the enriched entry (e.g. "LLM rule evaluation: ssrf_java"). */
  label: string;
  stage: Stage;
  /** Index of the source entry in the original entries array. */
  entryIndex: number;
  startMs: number;
  endMs: number;
  /**
   * Start of the actual execution window (endMs - execution_duration_ms),
   * or null when the backend did not stamp an execution duration. The span
   * before this point is time spent queued / blocked on dependencies.
   */
  execStartMs: number | null;
  status: string | null;
  /** Rule outcome (PASS/FAIL/...) when this task is an individual rule execution. */
  outcome: string | null;
}

export interface GanttTick {
  ms: number;
  label: string;
}

export interface GanttModel {
  tasks: GanttTask[];
  /** Overall time bounds across all tasks. */
  minMs: number;
  maxMs: number;
  totalMs: number;
  ticks: GanttTick[];
}

function toMs(iso: string | null): number | null {
  if (iso == null) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Format an offset from the timeline start as a compact axis label. */
export function formatTickLabel(offsetMs: number): string {
  if (offsetMs === 0) return '0s';
  const seconds = offsetMs / 1000;
  if (seconds < 60) return `${Number.isInteger(seconds) ? seconds : seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = Math.round(seconds % 60);
  return rem === 0 ? `${minutes}m` : `${minutes}m${rem}s`;
}

/** Candidate tick steps, in ms (1s .. 30m). */
const TICK_STEPS_MS = [
  1_000, 2_000, 5_000, 10_000, 15_000, 30_000, 60_000, 120_000, 300_000, 600_000, 1_800_000,
];

/** Pick a tick step that yields roughly 4-8 ticks across the span. */
export function pickTickStepMs(totalMs: number): number {
  for (const step of TICK_STEPS_MS) {
    if (totalMs / step <= 8) return step;
  }
  return TICK_STEPS_MS[TICK_STEPS_MS.length - 1];
}

/** Generate axis ticks (as offsets from minMs) covering the span. */
export function buildTicks(totalMs: number): GanttTick[] {
  if (totalMs <= 0) return [{ ms: 0, label: '0s' }];
  const step = pickTickStepMs(totalMs);
  const ticks: GanttTick[] = [];
  for (let ms = 0; ms <= totalMs; ms += step) {
    ticks.push({ ms, label: formatTickLabel(ms) });
  }
  return ticks;
}

/**
 * Build the Gantt model from audit entries. Returns null when fewer than two
 * entries carry task timing (legacy trails, or nothing worth charting).
 * Entries without both created_at and completed_at are skipped; tasks are
 * sorted by start time so the waterfall reads top-to-bottom.
 */
export function buildGanttModel(entries: AuditEntry[]): GanttModel | null {
  const tasks: GanttTask[] = [];

  entries.forEach((entry, entryIndex) => {
    const enriched = parseEnrichedFields(entry.description);
    if (!enriched?.stage || !enriched.label) return;
    const startMs = toMs(enriched.createdAt);
    const endMs = toMs(enriched.completedAt);
    if (startMs == null || endMs == null || endMs < startMs) return;

    const execStartMs =
      enriched.executionDurationMs != null
        ? Math.max(startMs, endMs - enriched.executionDurationMs)
        : null;

    tasks.push({
      label: enriched.label,
      stage: enriched.stage,
      entryIndex,
      startMs,
      endMs,
      execStartMs,
      status: enriched.status,
      outcome: enriched.outcome,
    });
  });

  if (tasks.length < 2) return null;

  tasks.sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
  const minMs = Math.min(...tasks.map((t) => t.startMs));
  const maxMs = Math.max(...tasks.map((t) => t.endMs));
  const totalMs = Math.max(maxMs - minMs, 1);

  return { tasks, minMs, maxMs, totalMs, ticks: buildTicks(maxMs - minMs) };
}

/** Convert an absolute ms timestamp to a 0-100 percentage across the span. */
export function toPercent(model: GanttModel, ms: number): number {
  return ((ms - model.minMs) / model.totalMs) * 100;
}

export interface RuleEvalSummary {
  startMs: number;
  endMs: number;
  total: number;
  passed: number;
  failed: number;
  /** Neither PASS nor FAIL: indeterminate, errored, skipped, etc. */
  other: number;
}

/** Tasks that are individual rule executions (they carry a rule outcome). */
export function isRuleEvalTask(task: GanttTask): boolean {
  return task.outcome != null;
}

/**
 * Aggregate the individual rule-execution tasks into a single collapsed-row
 * summary: overall span plus pass/fail/other counts. Returns null when the
 * model has no rule-execution tasks.
 */
export function summarizeRuleEvals(tasks: GanttTask[]): RuleEvalSummary | null {
  const ruleTasks = tasks.filter(isRuleEvalTask);
  if (ruleTasks.length === 0) return null;
  let passed = 0;
  let failed = 0;
  let other = 0;
  for (const task of ruleTasks) {
    const outcome = task.outcome?.toUpperCase();
    if (outcome === 'PASS') passed += 1;
    else if (outcome === 'FAIL') failed += 1;
    else other += 1;
  }
  return {
    startMs: Math.min(...ruleTasks.map((t) => t.startMs)),
    endMs: Math.max(...ruleTasks.map((t) => t.endMs)),
    total: ruleTasks.length,
    passed,
    failed,
    other,
  };
}
